import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, CalendarBlank, CaretDown } from "@phosphor-icons/react";
import { fmtDate } from "../lib/helpers";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { format } from "date-fns";

// Admin: colour by status. Photographer: colour by urgency category.
const STATUS_COLORS = { scheduled: "#B08D57", in_progress: "#4A0000" };
const CAT_COLORS = { today: "#1F9D55", upcoming: "#B08D57", in_progress: "#4A0000" };

const pin = (color) =>
  L.divIcon({
    className: "reelty-pin",
    html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 22],
    popupAnchor: [0, -22],
  });

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr.slice(0, 10) + "T00:00:00");
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
};

const photogCategory = (p) => {
  if (p.status === "in_progress") return "in_progress";
  return daysUntil(p.shoot_date) === 0 ? "today" : "upcoming";
};

const CAT_LABEL = {
  today: "Today's shoot",
  upcoming: "Upcoming",
  in_progress: "In progress — upload raws",
};

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lon], 12);
    } else if (points.length > 1) {
      map.fitBounds(points.map((p) => [p.lat, p.lon]), { padding: [50, 50], maxZoom: 13 });
    }
  }, [points, map]);
  return null;
}

export default function ShootsMap({ properties, onOpen, variant = "admin" }) {
  const isPhotog = variant === "photographer";
  const [range, setRange] = useState(null); // {from, to} Date objects; null = all days
  const [mode, setMode] = useState("single"); // single | range

  // Shoots with coordinates that are still active (scheduled/in_progress).
  const active = useMemo(
    () => properties.filter((p) => ["scheduled", "in_progress"].includes(p.status) && p.lat != null && p.lon != null),
    [properties]
  );

  // Photographer: only next-7-day shoots, plus any in-progress (raws still pending).
  const inWindow = useMemo(() => {
    if (!isPhotog) return active;
    return active.filter((p) => {
      if (p.status === "in_progress") return true;
      const du = daysUntil(p.shoot_date);
      return du != null && du >= 0 && du <= 7;
    });
  }, [active, isPhotog]);

  const rangeStr = useMemo(() => {
    if (!range?.from) return null;
    return { from: format(range.from, "yyyy-MM-dd"), to: format(range.to || range.from, "yyyy-MM-dd") };
  }, [range]);

  const shown = useMemo(() => {
    if (isPhotog || !rangeStr) return inWindow;
    return inWindow.filter((p) => {
      const dk = (p.shoot_date || "").slice(0, 10);
      return dk && dk >= rangeStr.from && dk <= rangeStr.to;
    });
  }, [inWindow, rangeStr, isPhotog]);

  const filterLabel = !rangeStr
    ? "All days"
    : rangeStr.from === rangeStr.to
      ? fmtDate(rangeStr.from)
      : `${fmtDate(rangeStr.from)} – ${fmtDate(rangeStr.to)}`;

  const center = shown[0] ? [shown[0].lat, shown[0].lon] : [39.8283, -98.5795];
  const colorFor = (p) => (isPhotog ? CAT_COLORS[photogCategory(p)] : STATUS_COLORS[p.status] || "#4A0000");

  return (
    <div className="mb-8 bg-white border border-[#E9DDC6] rounded-md overflow-hidden relative z-0 isolate" data-testid="shoots-map">
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#E9DDC6] flex-wrap">
        <div className="flex items-center gap-2">
          <MapPin size={18} weight="duotone" color="#4A0000" />
          <span className="font-display text-xl">{isPhotog ? "My Shoots — Next 7 Days" : "Shoot Map"}</span>
          <span className="text-xs text-[#5C5A58]">· {shown.length} {shown.length === 1 ? "shoot" : "shoots"}</span>
        </div>
        {isPhotog ? (
          <div className="flex items-center gap-4 flex-wrap" data-testid="map-legend">
            {["today", "upcoming", "in_progress"].map((c) => (
              <span key={c} className="flex items-center gap-1.5 text-xs text-[#5C5A58]">
                <span className="w-3 h-3 rounded-full inline-block" style={{ background: CAT_COLORS[c] }} />
                {CAT_LABEL[c]}
              </span>
            ))}
          </div>
        ) : (
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="map-day-filter"
                className="flex items-center gap-2 text-sm border border-[#E9DDC6] rounded-md px-3 py-1.5 bg-white hover:border-[#4A0000] transition-colors">
                <CalendarBlank size={16} weight="duotone" color="#5C5A58" />
                <span className={rangeStr ? "text-[#1A1A1A]" : "text-[#5C5A58]"}>{filterLabel}</span>
                <CaretDown size={12} weight="bold" className="text-[#9A968E]" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white border-[#E9DDC6]" align="end" data-testid="map-day-popover">
              <div className="flex items-center gap-1 p-2 border-b border-[#F0ECE2]">
                <button data-testid="map-mode-single" onClick={() => setMode("single")}
                  className={`text-xs px-2.5 py-1 rounded-md transition-colors ${mode === "single" ? "bg-[#4A0000] text-white" : "text-[#5C5A58] hover:bg-[#FAF9F6]"}`}>Single day</button>
                <button data-testid="map-mode-range" onClick={() => setMode("range")}
                  className={`text-xs px-2.5 py-1 rounded-md transition-colors ${mode === "range" ? "bg-[#4A0000] text-white" : "text-[#5C5A58] hover:bg-[#FAF9F6]"}`}>Date range</button>
                <button data-testid="map-filter-clear" onClick={() => setRange(null)}
                  className="ml-auto text-xs text-[#4A0000] px-2 py-1 rounded-md hover:bg-[#FAF9F6]">All days</button>
              </div>
              {mode === "single"
                ? <Calendar mode="single" selected={range?.from} onSelect={(d) => setRange(d ? { from: d, to: d } : null)} initialFocus />
                : <Calendar mode="range" selected={range || undefined} onSelect={(r) => setRange(r || null)} initialFocus />}
            </PopoverContent>
          </Popover>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="h-[360px] flex flex-col items-center justify-center text-[#5C5A58] bg-[#FAF9F6]" data-testid="map-empty">
          <MapPin size={40} weight="duotone" className="mb-3 text-[#D1CCC2]" />
          <p className="text-sm">{isPhotog ? "No shoots in the next 7 days." : `No active shoots to map${rangeStr ? " in this date range" : ""}.`}</p>
        </div>
      ) : (
        <MapContainer center={center} zoom={5} scrollWheelZoom={false} style={{ height: "360px", width: "100%", zIndex: 0 }}>
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={shown} />
          {shown.map((p) => {
            const cat = photogCategory(p);
            return (
              <Marker key={p.id} position={[p.lat, p.lon]} icon={pin(colorFor(p))}>
                <Popup>
                  <div className="space-y-1" style={{ minWidth: 160 }}>
                    <p className="font-semibold text-[#1A1A1A]">{p.title}</p>
                    <p className="text-xs text-[#5C5A58]">{p.address}</p>
                    <p className="text-xs text-[#5C5A58]">
                      {fmtDate(p.shoot_date)} · {isPhotog ? CAT_LABEL[cat] : (p.status === "in_progress" ? "In progress" : "Scheduled")}
                    </p>
                    <button data-testid={`map-open-${p.id}`} onClick={() => onOpen(p.id)}
                      className="mt-1 w-full bg-[#4A0000] text-white rounded py-1 text-xs font-medium hover:bg-[#5C1A1A]">
                      View shoot
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      )}
    </div>
  );
}
